import { DEFAULT_EVENT_CURRENCY_CODE } from '@/config/appDefaults';

/**
 * Fallback AZN per 1 EUR when `GET /exchange-rates/current?to=EUR` is unavailable.
 * Prefer live {@link ConvertCurrencyAmountOptions.eurPerAzn} from the API (EUR per 1 AZN).
 */
export const AZN_PER_EUR_RATE = 1.85;
/**
 * Fallback RUB ↔ AZN when `GET /exchange-rates/current` is unavailable (offline / misconfig).
 * 1 RUB ~= 0.0205 AZN — prefer live {@link ConvertCurrencyAmountOptions.rubPerAzn} from the API.
 */
export const AZN_PER_RUB_RATE = 0.0205;

export function aznToEurAmount(
  amountAzn: number,
  eurPerAzn?: number | null,
): number {
  const live =
    typeof eurPerAzn === "number" && Number.isFinite(eurPerAzn) && eurPerAzn > 0
      ? eurPerAzn
      : null;
  if (live !== null) return amountAzn * live;
  return amountAzn / AZN_PER_EUR_RATE;
}

export function eurToAznAmount(
  amountEur: number,
  eurPerAzn?: number | null,
): number {
  const live =
    typeof eurPerAzn === "number" && Number.isFinite(eurPerAzn) && eurPerAzn > 0
      ? eurPerAzn
      : null;
  if (live !== null) return amountEur / live;
  return amountEur * AZN_PER_EUR_RATE;
}

export function rubToAznAmount(amountRub: number): number {
  return amountRub * AZN_PER_RUB_RATE;
}

export function aznToRubAmount(amountAzn: number): number {
  return amountAzn / AZN_PER_RUB_RATE;
}

export type ConvertCurrencyAmountOptions = {
  /**
   * RUB per 1 AZN from `GET /exchange-rates/current` (`rate` when from=AZN, to=RUB).
   * When omitted, AZN↔RUB uses {@link AZN_PER_RUB_RATE}.
   */
  rubPerAzn?: number | null;
  /**
   * EUR per 1 AZN from `GET /exchange-rates/current` (`rate` when from=AZN, to=EUR).
   * When omitted, AZN↔EUR uses {@link AZN_PER_EUR_RATE} (AZN per 1 EUR).
   */
  eurPerAzn?: number | null;
};

/**
 * Convert amount between AZN, EUR, and RUB.
 * AZN↔EUR and AZN↔RUB prefer live CBAR-backed rates when provided.
 * Unknown/unsupported pairs are returned unchanged.
 */
export function convertCurrencyAmount(
  amount: number,
  fromCurrencyCode: string,
  toCurrencyCode: string,
  options?: ConvertCurrencyAmountOptions,
): number {
  const from = fromCurrencyCode.toUpperCase();
  const to = toCurrencyCode.toUpperCase();
  if (from === to) return amount;
  const r = options?.rubPerAzn;
  const rubPerAzn =
    typeof r === "number" && Number.isFinite(r) && r > 0 ? r : null;
  const e = options?.eurPerAzn;
  const eurPerAzn =
    typeof e === "number" && Number.isFinite(e) && e > 0 ? e : null;
  if (from === "AZN" && to === "EUR") return aznToEurAmount(amount, eurPerAzn);
  if (from === "EUR" && to === "AZN") return eurToAznAmount(amount, eurPerAzn);
  if (from === "AZN" && to === "RUB") {
    if (rubPerAzn !== null) return amount * rubPerAzn;
    return aznToRubAmount(amount);
  }
  if (from === "RUB" && to === "AZN") {
    if (rubPerAzn !== null) return amount / rubPerAzn;
    return rubToAznAmount(amount);
  }
  return amount;
}

/** Одна строка «148 ₼ (80 €)» для подписей и юридических плейсхолдеров (EUR — целые). */
export function formatAznWithEurHint(amountAzn: number, locale: string): string {
  const azn = formatCurrencyAmount(amountAzn, 'AZN', locale);
  const eur = formatCurrencyAmount(Math.round(aznToEurAmount(amountAzn)), 'EUR', locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${azn} (${eur})`;
}

/**
 * Валюта события: при появлении поля `currency` в ответе GET /azdance/:slug оно имеет приоритет.
 * Иначе — константа `DEFAULT_EVENT_CURRENCY_CODE` в `appDefaults`.
 */
export function getEventCurrencyCode(event: unknown): string {
  const fallback = DEFAULT_EVENT_CURRENCY_CODE;
  if (event && typeof event === "object" && "currency" in event) {
    const c = (event as { currency?: string }).currency;
    if (typeof c === "string" && c.trim()) return c.trim().toUpperCase();
  }
  return fallback.toUpperCase();
}

/** Символ для подстановки в строки переводов (узкий символ валюты или код). */
export function getCurrencyDisplaySymbol(
  locale: string,
  currencyCode: string,
): string {
  try {
    const parts = new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    const cur = parts.find((p) => p.type === "currency");
    if (cur?.value) return cur.value;
  } catch {
    /* ignore */
  }
  return currencyCode;
}

export type FormatCurrencyAmountDigits = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export function formatCurrencyAmount(
  amount: number,
  currencyCode: string,
  locale: string,
  digits?: FormatCurrencyAmountDigits,
): string {
  const minimumFractionDigits = digits?.minimumFractionDigits ?? 0;
  const maximumFractionDigits = digits?.maximumFractionDigits ?? 2;
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currencyCode.toUpperCase(),
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(amount);
  } catch {
    return `${amount} ${currencyCode}`;
  }
}
